import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Permissions } from 'config';

import SnackbarCustom from '../../components/Snackbar/Snackbar.js';
import { AddAlert } from '@mui/icons-material';

import Step1 from './AddSteps/Step1.js';
import Step2 from './AddSteps/Step2.js';
import Step3 from './AddSteps/Step3.js';
import Step4 from './AddSteps/Step4.js';

import useSWR, { mutate } from 'swr';
import { useDispatch, useSelector } from 'react-redux';
import { getCategories } from '../../store/actions/category';
import { getCompanies, getCompanyById } from '../../store/actions/company';
import { fetcher, groupApi, offerApi } from '../../lib/api';
import customSelectStyle from 'assets/jss/material-dashboard-pro-react/customSelectStyle.js';
import customCheckboxRadioSwitch from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch.js';
import { FlexContainer } from 'components/FlexContainer/FlexContainer.jsx';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import styles from './addOffer.module.scss';
import { resolveImageForCreate } from '../../utils/remoteImageToDataUri';

const shimmerBase = {
    background: 'linear-gradient(90deg, rgba(222,44,72,0.06) 25%, rgba(222,44,72,0.14) 50%, rgba(222,44,72,0.06) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer-offer-form 1.4s ease-in-out infinite',
    borderRadius: '8px',
};

if (typeof document !== 'undefined' && !document.getElementById('shimmer-offer-form-style')) {
    const el = document.createElement('style');
    el.id = 'shimmer-offer-form-style';
    el.textContent = '@keyframes shimmer-offer-form { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }';
    document.head.appendChild(el);
}

const StepContentShimmer = ({ t }) => {
    const sTitle = { color: '#de2c48', fontWeight: 'bold', fontStyle: 'italic', fontSize: 'clamp(1rem, 2.5vw, 1.35rem)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6 };
    const sCard = {
        borderRadius: 10,
        padding: '16px 20px',
        background: 'var(--spara-surface, white)',
        color: 'var(--spara-text, inherit)',
        boxShadow: 'var(--spara-shadow, 0 2px 8px rgba(0,0,0,0.1))',
    };
    const subHead = {
        fontSize: '0.78rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--spara-text-muted, #888)',
        margin: '0 0 4px',
    };

    return (
        <div style={{ padding: '0 8px 8px' }}>
            <div style={{ marginBottom: 20 }}>
                <h4 style={sTitle}>{t('offer.text')}</h4>
                <div style={sCard}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0 12px', alignItems: 'start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <p style={subHead}>🇮🇸 {t('icelandic')}</p>
                            <div style={{ ...shimmerBase, height: 40, width: '100%' }} />
                            <div style={{ ...shimmerBase, height: 130, width: '100%' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, paddingTop: 28 }}>
                            <div style={{ ...shimmerBase, height: 28, width: 28, borderRadius: 6 }} />
                            <div style={{ ...shimmerBase, height: 28, width: 28, borderRadius: 6 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <p style={subHead}>🇬🇧 {t('english')}</p>
                            <div style={{ ...shimmerBase, height: 40, width: '100%' }} />
                            <div style={{ ...shimmerBase, height: 130, width: '100%' }} />
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
                <div>
                    <h4 style={sTitle}>{t('offer.images')}</h4>
                    <div style={sCard}>
                        <div style={{ ...shimmerBase, height: 200, width: '100%', borderRadius: 10, marginBottom: 10 }} />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                            <div style={{ ...shimmerBase, height: 80, borderRadius: 6 }} />
                            <div style={{ ...shimmerBase, height: 80, borderRadius: 6 }} />
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <h4 style={sTitle}>{t('offer.info')}</h4>
                        <div style={sCard}>
                            <div style={{ ...shimmerBase, height: 44, width: '100%', marginBottom: 12 }} />
                            <div style={{ ...shimmerBase, height: 44, width: '100%' }} />
                        </div>
                    </div>
                    <div>
                        <h4 style={sTitle}>{t('offer.type')}</h4>
                        <div style={sCard}><div style={{ ...shimmerBase, height: 44, width: '100%' }} /></div>
                    </div>
                    <div>
                        <h4 style={sTitle}>{t('offer.activation')}</h4>
                        <div style={sCard}><div style={{ ...shimmerBase, height: 44, width: '100%' }} /></div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <h4 style={sTitle}>{t('offer.active_when')}</h4>
                <div style={sCard}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
                        <div>
                            <p style={{ ...subHead, margin: '0 0 10px' }}>{t('offer.period')}</p>
                            <div style={{ ...shimmerBase, height: 32, width: '100%', marginBottom: 8 }} />
                            <div style={{ ...shimmerBase, height: 32, width: '100%' }} />
                        </div>
                        <div>
                            <p style={{ ...subHead, margin: '0 0 10px' }}>{t('offer.weekly_activation')}</p>
                            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                                <div key={d} style={{ ...shimmerBase, height: 28, width: '100%', marginBottom: 6 }} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const style = {
    inputAdornment: { position: 'relative' },
    dSelected: { color: '#DE2C48', backgroundColor: '#FBEAEC' },
    selectMenu: { color: '#DE2C48', fontSize: '14px' },
    select: { color: '#666B77', fontSize: '14px' },
    txtArea: {
        height: '84px', width: '100%',
        borderColor: '#CCCDD1',
        fontSize: '1rem', fontFamily: 'Roboto, Helvetica, Arial',
        fontWeight: 400, resize: 'none',
        borderWidth: 1, borderStyle: 'solid', borderRadius: 8, padding: '4px 8px',
    },
    txtAreaError: { borderColor: '#DE2C48' },
    txtAreaSuccess: { borderColor: '#00A86B' },
    ...customSelectStyle,
    ...customCheckboxRadioSwitch,
};

const useStyles = createUseStyles(style);

const AddOffer = ({ t }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const classes = useStyles();
    const [notification, setNotification] = useState({ status: false, type: '', message: '' });
    const [loadingSave, setLoadingSave] = useState(false);
    const isDirtyRef = useRef(false);
    const [blockedUrl, setBlockedUrl] = useState(null);
    const [showNavBlocker, setShowNavBlocker] = useState(false);
    const initialStep1StateRef = useRef(undefined);

    const markDirty = (val) => {
        isDirtyRef.current = val;
    };
    const { categories, loadingCat } = useSelector((state) => state.category);
    const { company, companies, loading } = useSelector((state) => state.company);

    const step1Ref = useRef(null);
    const step2Ref = useRef(null);
    const step3Ref = useRef(null);
    const step4Ref = useRef(null);
    const topRef = useRef(null);

    const currentStep = Math.min(
        Math.max(parseInt(searchParams.get('step') || '1', 10), 1),
        4
    ) - 1;
    const setCurrentStep = (valueOrFn) => {
        const next = typeof valueOrFn === 'function' ? valueOrFn(currentStep) : valueOrFn;
        setSearchParams((prev) => { prev.set('step', String(next + 1)); return prev; }, { replace: false });
    };
    const [liveStep1State, setLiveStep1State] = useState(null);
    const [liveStep2State, setLiveStep2State] = useState(null);

    const { data: specialCompanies, isLoading: loadingSpec } = useSWR(
        groupApi.list({ expand: 'group' }),
        fetcher
    );

    const isUpdating = !!(id && id !== 'new');
    const duplicateFromId = searchParams.get('duplicateFrom');
    const isDuplicating = !isUpdating && !!duplicateFromId;

    const { data: offerByIdData, error: offerByIdError, isLoading: loadingOfferById, mutate: mutateOffer } = useSWR(
        isUpdating ? offerApi.getById(id) : isDuplicating ? offerApi.getById(duplicateFromId) : null,
        fetcher
    );
    const offerById = offerByIdData?.results ?? {};
    let store = JSON.parse(localStorage.getItem('user'));
    let user_is_group_admin = !!(store && store.roles.includes('group_admin'));
    const user_is_company_user = !!(store && store.roles.includes('company_user'));
    const isAdmin = !!(store && store.permission && store.permission.includes(Permissions.OFFER_CREATE));

    const allowedCompanyIds = store.companies ? store.companies.map((c) => c.id) : [];
    const allowedGroupIds = (store.groupIds || []).filter(Boolean);

    // Every offer requires an explicit distribution/group selection. Step 2 must
    // remain visible even for group admins with only one group, since cached group
    // ids can refer to groups that have since been hidden or deleted.
    const toActualStep = (visIdx) => visIdx;

    useEffect(() => {
        dispatch(getCategories());
        if (user_is_group_admin || isAdmin) dispatch(getCompanies());
    }, [dispatch]);

    useEffect(() => {
        if (liveStep1State === null) return;
        if (initialStep1StateRef.current === undefined) {
            initialStep1StateRef.current = liveStep1State;
            return;
        }
        markDirty(true);
    }, [liveStep1State]);

    const getData = (data) => dispatch(getCompanyById(data));
    const onRefreshCompanies = () => dispatch(getCompanies());

    const allStepRefs = [step1Ref, step2Ref, step3Ref, step4Ref];

    // Scroll the window back to the top of the form container when the
    // user advances or returns to a step.
    //
    // Notes / browser quirks:
    //   • `currentStep` is derived from the URL search params, so calling
    //     scroll logic *immediately* after `setCurrentStep` runs before
    //     React has committed the new step into the DOM. On Windows
    //     Chromium this leaves the user staring at a tall, blank-looking
    //     viewport at the previous scroll position.
    //   • `scrollIntoView({ behavior: 'smooth' })` is also unreliable on
    //     Windows Chromium when invoked right after a render — the
    //     smooth request can be silently dropped.
    //
    // The robust fix is to scroll inside a `useEffect` that watches
    // `currentStep`, and to use `window.scrollTo` with an absolute Y
    // target (Chromium honours this reliably across platforms). We keep
    // a `scrollToTop` helper so explicit callers can still trigger a
    // scroll (e.g. validation-failure paths), but the primary trigger
    // now lives in the effect below.
    // Walk up the DOM tree from `el` and find the nearest scrollable
    // ancestor. We have to consider two different setups:
    //
    //   • Non-Windows: the admin layout's `mainPanel` div has
    //     `overflow: auto`. Walking up and matching auto/scroll picks
    //     it up.
    //   • Windows: the same `mainPanel` is wrapped by PerfectScrollbar
    //     which forces `overflow: hidden !important` and scrolls the
    //     element via direct `scrollTop` manipulation. We therefore
    //     also accept `overflow: hidden` containers whose content
    //     exceeds their box (`scrollHeight > clientHeight`).
    //
    // Returning the right element here is the whole game — once we
    // have it, setting `scrollTop = 0` works identically across
    // platforms.
    const getScrollableAncestor = (el) => {
        let node = el && el.parentElement;
        while (node && node !== document.documentElement) {
            const style = window.getComputedStyle(node);
            const overflowY = style.overflowY;
            const isScrollish =
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay' ||
                overflowY === 'hidden';
            if (isScrollish && node.scrollHeight > node.clientHeight + 1) {
                return node;
            }
            node = node.parentElement;
        }
        return null;
    };

    const performScrollToTop = () => {
        const el = topRef.current;
        if (!el) return;

        const container = getScrollableAncestor(el);

        if (container) {
            // Snap to 0 directly — instant and reliable across every
            // browser, even when PerfectScrollbar has hijacked the
            // container. Smooth animation isn't worth the risk of the
            // request being silently dropped on Windows Chromium after a
            // state-driven re-render.
            container.scrollTop = 0;

            // Hint to any custom scrollbar implementation (PerfectScroll-
            // bar) that the position changed so it can resync its
            // visuals.
            try {
                container.dispatchEvent(new Event('scroll'));
            } catch {
                /* IE / legacy — fine to ignore. */
            }
        } else {
            // Fallback: no nested scroll container — let the window or
            // the document element handle it. Used on mobile where the
            // mainPanel is set to overflow:visible.
            try {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            } catch {
                window.scrollTo(0, 0);
            }
            if (document.scrollingElement) {
                document.scrollingElement.scrollTop = 0;
            }
        }
    };

    const scrollToTop = () => {
        // Synchronous — callers that want to scroll outside of a step
        // transition (e.g. on validation failure) can use this directly.
        performScrollToTop();
    };

    // Skip the very first scroll-to-top so we don't yank the user up
    // on initial mount (e.g. when they deep-link to /offers/new?step=2).
    //
    // We use useLayoutEffect (not useEffect) so that we run after the
    // DOM is committed but BEFORE the browser paints. This eliminates
    // the visual flash where the user sees a blank viewport at the old
    // scroll position before being scrolled to the top.
    const didMountStepEffect = useRef(false);
    useLayoutEffect(() => {
        if (!didMountStepEffect.current) {
            didMountStepEffect.current = true;
            return;
        }
        performScrollToTop();
    }, [currentStep]);

    const validateUpTo = (targetVisibleStep) => {
        for (let i = currentStep; i < targetVisibleStep; i++) {
            const ref = allStepRefs[toActualStep(i)];
            if (ref.current && ref.current.isValidated && ref.current.isValidated() === false) {
                return false;
            }
        }
        return true;
    };

    const goNext = () => {
        if (!validateUpTo(currentStep + 1)) return;
        markDirty(true);
        setCurrentStep((s) => s + 1);
        scrollToTop();
    };

    const goPrev = () => {
        setCurrentStep((s) => s - 1);
        scrollToTop();
    };

    const goToStep = (i) => {
        if (i > currentStep && !validateUpTo(i)) return;
        setCurrentStep(i);
        scrollToTop();
    };

    const onSave = async () => {
        if (loadingSave) return;
        if (!validateUpTo(currentStep + 1)) return;

        const about = step1Ref.current ? step1Ref.current.sendState() : {};
        const groups = step2Ref.current ? step2Ref.current.sendState() : {};
        const category = step3Ref.current ? step3Ref.current.sendState() : {};
        const confirmation = step4Ref.current ? step4Ref.current.sendState() : {};

        let data = { about, groups, category };

        // EN → IS fallback: if the Icelandic title/description are empty
        // but the English ones are filled, copy the English text into the
        // Icelandic fields before sending. The user is warned about this
        // in Step1 (offer.text_en_only_warning).
        if (
            !(data.about.title || '').trim() &&
            (data.about.title_en || '').trim()
        ) {
            data.about.title = data.about.title_en;
        }
        if (
            !(data.about.description || '').trim() &&
            (data.about.description_en || '').trim()
        ) {
            data.about.description = data.about.description_en;
        }

        if (data.about.discount === 'discount' || data.about.discount === 'price') {
            const discountRaw = (data.about.discountInput || '').trim();
            if (discountRaw) {
                let discount = discountRaw;
                if (data.about.discount === 'discount') {
                    if (discount.slice(0, 1) !== '-') discount = '-' + discount;
                    if (discount.slice(-1) !== '%') discount = discount + '%';
                } else {
                    discount = discount.replace('kr.', '').replace('kr', '').trim() + ' kr.';
                }
                data.about.discount = discount;
            } else {
                data.about.discount = '';
            }
        }

        data.about.torgid_offer_active_times = data.about.torgid_offer_active_times.filter(
            (el) => el.active === true
        );

        const groupIds = data.groups?.torgid_special_company_ids || [];
        if (groupIds.length === 1 && groupIds[0] === 0) {
            data.about.torgid_special_company_id = null;
            data.about.group_ids = null;
        } else {
            data.about.group_ids = groupIds.join(',');
            data.about.torgid_special_company_id = 999;
        }

        // approved lives in step4 (staðfesting)
        data.about.approved = confirmation?.approved || false;

        setLoadingSave(true);
        try {
            if (!isUpdating) {
                data.about.image = await resolveImageForCreate(data.about.image);
                data.about.images = await Promise.all(
                    (data.about.images || [])
                        .filter((item) => item?.image)
                        .map(async (item) => ({
                            image: await resolveImageForCreate(item.image),
                            sort_order: item.sort_order,
                        }))
                );
            }

            const url = isUpdating ? offerApi.update(id) : offerApi.create();
            const method = isUpdating ? 'PUT' : 'POST';
            await fetcher(url, method, {
                torgid_company_id: data.about.companyId,
                torgid_offer_categories: data.category.torgid_category_id,
                ...data.about,
            });
            if (isUpdating) mutateOffer();
            mutate((key) => typeof key === 'string' && key.startsWith('/offers'));
            markDirty(false);
            initialStep1StateRef.current = undefined;
            navigate('/offers');
        } catch (error) {
            const res = error?.response?.data;
            const message = res?.results ? res.results[Object.keys(res.results)[0]] : '';
            setNotification({ status: true, type: 'error', message: (res?.message || '') + (message ? '. ' + message : '') });
        } finally {
            setLoadingSave(false);
        }
    };

    useEffect(() => {
        const originalPushState = window.history.pushState;
        let isInternal = false;

        window.history.pushState = function (state, title, url) {
            if (isInternal || !isDirtyRef.current) {
                originalPushState.call(this, state, title, url);
                return;
            }
            try {
                const next = new URL(url, window.location.origin);
                if (next.pathname === window.location.pathname) {
                    originalPushState.call(this, state, title, url);
                    return;
                }
            } catch { /* ignore */ }
            setBlockedUrl(url?.toString() ?? null);
            setShowNavBlocker(true);
        };

        const handlePopState = () => {
            if (!isDirtyRef.current) return;
            isInternal = true;
            originalPushState.call(window.history, null, '', window.location.href);
            isInternal = false;
            setBlockedUrl(null);
            setShowNavBlocker(true);
        };

        const handleBeforeUnload = (e) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.history.pushState = originalPushState;
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const confirmLeave = useCallback(() => {
        markDirty(false);
        setShowNavBlocker(false);
        if (blockedUrl) navigate(blockedUrl);
    }, [blockedUrl, navigate]);

    const stayOnPage = useCallback(() => {
        setShowNavBlocker(false);
        setBlockedUrl(null);
    }, []);

    const offerData = useMemo(() => {
        if (isUpdating) {
            return offerById;
        }
        if (!isDuplicating || !offerById?.id) {
            return {};
        }

        const groupIds = offerById.group_ids
            ? offerById.group_ids
                  .split(',')
                  .map((x) => parseInt(x, 10))
                  .filter(Boolean)
            : [];
        const groups = specialCompanies?.results || [];
        const approved =
            groupIds.length > 0 &&
            groupIds.every(
                (gid) => groups.find((g) => g.id === gid)?.auto_approve_offers
            );

        return {
            ...offerById,
            id: undefined,
            isDuplicate: true,
            approved: Boolean(approved),
            notified: false,
            published_at: null,
            created_at: undefined,
            updated_at: undefined,
        };
    }, [isUpdating, isDuplicating, offerById, specialCompanies]);

    const previewOffer = useMemo(() => {
        const base = offerData || {};
        const specialIds = [0, 1, 3];

        // ── Step1 fields ──────────────────────────────────────
        let discountStr = base.discount;
        let liveTitle = base.title;
        let liveTitleEn = base.title_en;
        let liveImage = base.image;
        let liveCompany = base.torgid_company || null;

        if (liveStep1State) {
            const raw = (liveStep1State.discountInput || '').trim();
            if (raw) {
                if (liveStep1State.discount === 'discount') {
                    let d = raw.startsWith('-') ? raw : '-' + raw;
                    if (!d.endsWith('%')) d = d + '%';
                    discountStr = d;
                } else if (liveStep1State.discount === 'price') {
                    discountStr = raw.replace('kr.', '').trim() + ' kr.';
                }
            }
            liveTitle = liveStep1State.title ?? base.title;
            liveTitleEn = liveStep1State.title_en ?? base.title_en;
            liveImage = liveStep1State.image || base.image;
            const companyId = liveStep1State.companyId;
            liveCompany =
                (companyId && companies?.results?.find((c) => c.id === companyId)) ||
                company ||
                base.torgid_company ||
                null;
        }

        // ── Step2 fields ──────────────────────────────────────
        let liveSpecialCompany = base.torgid_special_company ?? null;
        let liveGroupIds = base.group_ids ?? null;

        if (liveStep2State) {
            const selectedIds = liveStep2State.torgid_special_company_ids ?? [];
            const realGroupIds = selectedIds.filter((id) => !specialIds.includes(id));
            const hasSpecialId = selectedIds.some((id) => id === 1 || id === 3);

            if (realGroupIds.length > 0) {
                const firstGroup = specialCompanies?.results?.find((g) => g.id === realGroupIds[0]);
                liveSpecialCompany = firstGroup || null;
                liveGroupIds = realGroupIds.join(',');
            } else if (hasSpecialId) {
                const specialId = selectedIds.find((id) => id === 1 || id === 3);
                liveSpecialCompany = specialCompanies?.results?.find((g) => g.id === specialId) || null;
                liveGroupIds = selectedIds.join(',');
            } else {
                liveSpecialCompany = null;
                liveGroupIds = null;
            }
        }

        return {
            ...base,
            title: liveTitle,
            title_en: liveTitleEn,
            discount: discountStr,
            image: liveImage,
            torgid_company: liveCompany,
            torgid_special_company: liveSpecialCompany,
            group_ids: liveGroupIds,
        };
    }, [liveStep1State, liveStep2State, offerData, company, companies, specialCompanies]);

    // Only block step content on the initial data load.
    // getCompanyById also sets loading:true but should NOT unmount the form.
    const companiesListLoading = (user_is_group_admin || isAdmin) && loading && !companies?.results;
    const isDataLoading = loadingCat || loadingSpec || ((isUpdating || isDuplicating) && loadingOfferById) || companiesListLoading;

    const offerGroupIds = offerById?.group_ids ? offerById.group_ids.split(',').map(Number) : [];
    const hasAccess =
        (!isUpdating && !isDuplicating) ||
        isAdmin ||
        allowedCompanyIds.includes(offerById?.torgid_company?.id) ||
        allowedGroupIds.some((gid) => offerGroupIds.includes(gid));

    if ((isUpdating || isDuplicating) && !loadingOfferById) {
        const offerStatus = offerByIdError?.response?.status;
        const offerMissing = !offerById?.id;
        const forbidden = offerStatus === 403 || (offerById?.id && !hasAccess);
        const notFound = offerStatus === 404 || (offerMissing && !offerByIdError);

        if (forbidden || notFound || offerByIdError || offerMissing) {
            const title = notFound && !forbidden ? t('offer.not_found_title') : t('offer.no_access_title');
            const message =
                notFound && !forbidden ? t('offer.not_found_message') : t('offer.no_access_message');

            return (
                <FlexContainer column style={{ justifyContent: 'center', alignItems: 'center', padding: 40, gap: 16 }}>
                    <SparaTitle title={title} />
                    <p style={{ fontSize: '1.1rem', color: '#666B77', textAlign: 'center', maxWidth: 480 }}>
                        {message}
                    </p>
                    <Button component={Link} to="/offers" color="primary">{t('back_to_offers')}</Button>
                </FlexContainer>
            );
        }
    }

    const selectedCompanyId = liveStep1State?.companyId
        || ((isUpdating || isDuplicating) && offerById?.torgid_company?.id)
        || (user_is_company_user ? store.user?.torgid_company_id : null);

    const userHasCompanyAccess = isAdmin || (selectedCompanyId && allowedCompanyIds.includes(selectedCompanyId));

    const sharedProps = {
        classes,
        offerData,
        categories: categories || {},
        data: specialCompanies || {},
        data2: company || {},
        getData,
        onRefreshCompanies,
        companies: companies || { results: [] },
        isAdmin,
        currentLang: localStorage.getItem('lang') || 'is',
        userHasCompanyAccess,
        // Live action_type from Step1 (falls back to the saved offer while Step1 hasn't mounted
        // yet) so Step2 can cross-validate the selected target groups against it.
        actionType: liveStep1State?.action_type ?? offerData?.action_type,
    };

    const allStepDefs = [
        { label: t('offer') },
        { label: t('special_companies') },
        { label: t('categories') },
        { label: t('confirmation') },
    ];
    const stepDefs = allStepDefs;

    return (
        <div ref={topRef} data-testid="offer-wizard">
            <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 20px' }}>
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.status}
                closeNotification={() => setNotification({ status: false, type: '', message: '' })}
            />

            <GridContainer>
                <GridItem xs={12}>
                    <SparaTitle
                        title={isUpdating ? t('offer.update') : isDuplicating ? t('offer.duplicate_title') : t('offer.build')}
                    />
                </GridItem>

                <GridItem xs={12}>
                    {/* Step indicator */}
                    <div className={styles.stepper}>
                        {stepDefs.map((step, i) => (
                            <React.Fragment key={i}>
                                <div
                                    className={styles.stepItem}
                                    style={{
                                        justifyContent: i === 0 ? 'flex-start' : i === stepDefs.length - 1 ? 'flex-end' : 'center',
                                    }}
                                    onClick={() => { if (i !== currentStep) goToStep(i); }}>
                                    <div className={`${styles.stepCircle} ${i === currentStep ? styles.stepCircleActive : ''} ${i < currentStep ? styles.stepCircleDone : ''}`}>
                                        {i < currentStep ? '✓' : i + 1}
                                    </div>
                                    <span className={`${styles.stepLabel} ${i === currentStep ? styles.stepLabelActive : ''} ${i < currentStep ? styles.stepLabelDone : ''}`}>
                                        {step.label}
                                    </span>
                                </div>
                                {i < stepDefs.length - 1 && (
                                    <div className={`${styles.stepLine} ${i < currentStep ? styles.stepLineDone : ''}`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Step content — shimmer while loading, real steps once ready */}
                    <div className={styles.stepContent}>
                        {isDataLoading ? (
                            <StepContentShimmer t={t} />
                        ) : (
                            <>
                                <div style={{ display: toActualStep(currentStep) === 0 ? 'block' : 'none' }}>
                                    <Step1 ref={step1Ref} {...sharedProps} onStateChange={setLiveStep1State} />
                                </div>
                                <div style={{ display: toActualStep(currentStep) === 1 ? 'block' : 'none' }}>
                                    <Step2 ref={step2Ref} {...sharedProps} onStateChange={setLiveStep2State} />
                                </div>
                                <div style={{ display: toActualStep(currentStep) === 2 ? 'block' : 'none' }}>
                                    <Step3 ref={step3Ref} {...sharedProps} />
                                </div>
                                <div style={{ display: toActualStep(currentStep) === 3 ? 'block' : 'none' }}>
                                    <Step4 ref={step4Ref} {...sharedProps} offerData={previewOffer} />
                                </div>
                            </>
                        )}
                    </div>
                </GridItem>
            </GridContainer>
            </div>

            {/* Navigation — full width outside maxWidth content */}
            <div
                className={styles.bottomNav}
                data-testid="offer-wizard-nav"
                style={isDataLoading ? { visibility: 'hidden' } : {}}
            >
                <div>
                    {currentStep > 0 && (
                        <button
                            className={`${styles.navBtn} ${styles.navBtnPrev}`}
                            data-testid="offer-wizard-prev"
                            onClick={goPrev}
                        >
                            ← {t('previous')}
                        </button>
                    )}
                </div>
                <div>
                    {currentStep < stepDefs.length - 1 ? (
                        <button
                            className={`${styles.navBtn} ${styles.navBtnNext}`}
                            data-testid="offer-wizard-next"
                            onClick={goNext}
                        >
                            {t('next')} →
                        </button>
                    ) : (
                        <button
                            className={`${styles.navBtn} ${styles.navBtnSave}`}
                            data-testid="offer-wizard-save"
                            onClick={onSave}
                            disabled={loadingSave}
                        >
                            {loadingSave ? t('saving') : t('save')}
                        </button>
                    )}
                </div>
            </div>

            <Dialog open={showNavBlocker} maxWidth="xs" fullWidth>
                <DialogTitle>{t('unsaved_changes_title')}</DialogTitle>
                <DialogContent>
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--spara-text, #333)' }}>
                        {t('unsaved_changes_message')}
                    </p>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={stayOnPage}>{t('unsaved_stay')}</MuiButton>
                    <MuiButton onClick={confirmLeave} color="error">
                        {t('unsaved_leave')}
                    </MuiButton>
                    <MuiButton
                        onClick={async () => {
                            await onSave();
                            confirmLeave();
                        }}
                        variant="contained"
                        color="success"
                    >
                        {t('save')}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withTranslation('', { withRef: true })(AddOffer);
